import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import { theme } from '@Guidelines/Theme/theme';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ConfirmDialog } from '@verclam/ui-kit';

const errorMessageStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: inherit;
`;

const actionsRowStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	margin-top: 0.25rem;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly localInterviewId: string;
	readonly isNotes: boolean;
};

/**
 * Leaf action component (`guides/pages.md`) for a local interview whose consent was collected but
 * whose capture was never carried through (`localInterviewCaptureState` === `'capturable'`): it reads
 * its own identifier and navigates/dispatches itself, no `onResume`/`onDelete` callback. There is no
 * queue item yet — a second capture on this same interview would create one and let the server
 * `upsert` overwrite the first, renumbered from zero, destroying any audio already received, which is
 * exactly why this interview must resume on its own local id rather than start a fresh one. The
 * original consent instant survives the resume: it already lives on the local interview record and
 * is never re-collected here. "Supprimer" has nothing but local metadata to lose, so it is gated by a
 * plain `ConfirmDialog`, never `ConfirmDeleteDialog`.
 */
const InterviewCaptureActions: React.FC<Props> = ({ subject, localInterviewId, isNotes }) => {
	const localInterviewPort = useLocalInterviewPort();
	const navigate = useNavigate();
	const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = React.useState(false);
	const [actionErrorMessage, setActionErrorMessage] = React.useState<string | null>(null);

	const handleResumeClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/local-interviews/${localInterviewId}/recording`);
	}, [navigate, subject, localInterviewId]);

	const handleOpenDeleteConfirm = React.useCallback(() => {
		setActionErrorMessage(null);
		setIsDeleteConfirmVisible(true);
	}, []);

	const handleDeleteConfirmHide = React.useCallback(() => {
		setIsDeleteConfirmVisible(false);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		setActionErrorMessage(null);
		localInterviewPort.remove(localInterviewId).catch((reason: unknown) => {
			setActionErrorMessage(reason instanceof Error ? reason.message : 'La suppression a échoué.');
		});
		// Closes immediately regardless of the outcome above: waiting for the call to settle would
		// leave the dialog open long enough for a second click.
		setIsDeleteConfirmVisible(false);
	}, [localInterviewPort, localInterviewId]);

	return (
		<React.Fragment>
			{actionErrorMessage !== null ? <p css={errorMessageStyle}>{actionErrorMessage}</p> : null}
			<div css={actionsRowStyle}>
				<Button
					type="button"
					size="sm"
					severity="secondary"
					label="Reprendre l'enregistrement"
					onClick={handleResumeClick}
				/>
				<Button
					type="button"
					size="sm"
					severity="danger"
					label="Supprimer"
					onClick={handleOpenDeleteConfirm}
				/>
			</div>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isDeleteConfirmVisible}
				onHide={handleDeleteConfirmHide}
				header={isNotes ? 'Supprimer cette prise de notes ?' : 'Supprimer cet entretien ?'}
				message={
					isNotes
						? 'Cette prise de notes locale sera définitivement supprimée de cet appareil. Cette action est irréversible.'
						: 'Cet entretien local sera définitivement supprimé de cet appareil. Cette action est irréversible.'
				}
				acceptLabel="Supprimer définitivement"
				acceptSeverity="danger"
				defaultFocus="reject"
				accept={handleDeleteAccept}
			/>
		</React.Fragment>
	);
};

export default InterviewCaptureActions;
